import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, Save, Upload, Download, Eye, EyeOff, Move, QrCode } from 'lucide-react';
import { useAuth } from '../guardarToken/authContext';
import '../../components/diseñocss/ConfiguradorEtiquetas.css';


const TAMANOS_ETIQUETA = [
    { id: '100x50', label: '100 x 50 mm', width: 100, height: 50 },
    { id: '63x31', label: '63 x 31 mm', width: 63, height: 31 },
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizarTextoEtiqueta = (valor) => String(valor || '').trim();

const limpiarUbicacionDuplicada = (ubicacion, sector) => {
    const ubicacionNormalizada = normalizarTextoEtiqueta(ubicacion);
    const sectorNormalizado = normalizarTextoEtiqueta(sector);

    if (!ubicacionNormalizada || !sectorNormalizado) {
        return ubicacionNormalizada;
    }

    const ubicacionUpper = ubicacionNormalizada.toUpperCase();
    const sectorUpper = sectorNormalizado.toUpperCase();
    const sufijosDuplicados = [
        ` - ${sectorUpper}`,
        `, ${sectorUpper}`,
        ` ${sectorUpper}`,
    ];

    const sufijo = sufijosDuplicados.find((item) => ubicacionUpper.endsWith(item));
    if (!sufijo) {
        return ubicacionNormalizada;
    }

    return ubicacionNormalizada.slice(0, -sufijo.length).trim().replace(/[-,]$/, '').trim();
};

const getUbicacionEtiqueta = (pedido) => limpiarUbicacionDuplicada(pedido?.ubicacion, pedido?.sector);
const getDireccionEtiqueta = (pedido) => getUbicacionEtiqueta(pedido) || normalizarTextoEtiqueta(pedido?.sede_direccion);
const CAMPOS_FECHA_REQUERIDOS = [
    { id: 'fechaElaboracion', label: 'Elab.', x: 55, y: 28, fontSize: 9, visible: true, prefijo: 'Elaborado: ', rotation: 0 },
    { id: 'fechaVencimiento', label: 'Vto.', x: 55, y: 35, fontSize: 9, visible: true, prefijo: 'Consumir hasta: ', rotation: 0 },
];

const asegurarCamposFecha = (campos = []) => {
    const lista = Array.isArray(campos) ? [...campos] : [];
    CAMPOS_FECHA_REQUERIDOS.forEach((campoRequerido) => {
        if (!lista.some((campo) => campo.id === campoRequerido.id)) {
            lista.push({ ...campoRequerido });
        }
    });
    return lista.map((campo) => {
        if (campo.id === 'fechaElaboracion') {
            return { ...campo, prefijo: campo.prefijo || 'Elaborado: ' };
        }
        if (campo.id === 'fechaVencimiento') {
            return { ...campo, prefijo: campo.prefijo || 'Consumir hasta: ' };
        }
        return campo;
    });
};

const suavizarEscalaFuente = (scale) => {
    if (scale < 1) {
        return clamp(1 - ((1 - scale) * 0.45), 0.82, 1);
    }
    return 1 + ((scale - 1) * 0.9);
};

const normalizarConfigPlantilla = (rawCampos) => {
    try {
        const parsed = typeof rawCampos === 'string' ? JSON.parse(rawCampos) : rawCampos;
        const campos = Array.isArray(parsed)
            ? parsed
            : Array.isArray(parsed?.campos)
                ? parsed.campos
                : [];

        const tamanoEtiqueta = parsed?.tamanoEtiqueta || '100x50';

        return { campos, tamanoEtiqueta };
    } catch (error) {
        console.error('Error normalizando configuración de plantilla:', error);
        return { campos: [], tamanoEtiqueta: '100x50' };
    }
};

const ConfiguradorEtiquetas = ({ isOpen, onClose, onSaveConfig, pedidosReales = [] }) => {
    const { nombreUsuario } = useAuth();
    const [tamanoSeleccionado, setTamanoSeleccionado] = useState('100x50');
    const [tamanoAnterior, setTamanoAnterior] = useState('100x50');
    const tamanoActual = TAMANOS_ETIQUETA.find(t => t.id === tamanoSeleccionado) || TAMANOS_ETIQUETA[0];
    const tamanoAnt = TAMANOS_ETIQUETA.find(t => t.id === tamanoAnterior) || TAMANOS_ETIQUETA[0];
    const LABEL_WIDTH = tamanoActual.width;
    const LABEL_HEIGHT = tamanoActual.height;
    const DPI = 203;
    const apiUrlEtiquetas = process.env.REACT_APP_API_ETIQUETAS || '/api/menus/etiquetas/';


    const CAMPOS_DISPONIBLES = [
        { id: 'header', label: 'Encabezado (Def-Software)', tipo: 'texto' },
        { id: 'nombre', label: 'Nombre', tipo: 'dato' },
        { id: 'apellido', label: 'Apellido', tipo: 'dato' },
        { id: 'nombreCompleto', label: 'Nombre Completo', tipo: 'dato' },
        { id: 'plato', label: 'Plato', tipo: 'dato' },
        { id: 'tipoPlato', label: 'Tipo de Plato', tipo: 'dato' },
        { id: 'sector', label: 'Sector', tipo: 'dato' },
        { id: 'ubicacion', label: 'Ubicación', tipo: 'dato' },
        { id: 'sede', label: 'Sede', tipo: 'dato' },
        { id: 'sede_direccion', label: 'Dirección de Sede', tipo: 'dato' },
        { id: 'fechaElaboracion', label: 'Fecha Elaboración', tipo: 'dato' },
        { id: 'fechaVencimiento', label: 'Fecha Vencimiento', tipo: 'dato' },
        { id: 'separador', label: 'Línea Separadora', tipo: 'visual' },
        { id: 'qrCode', label: 'Código QR', tipo: 'qr' },
        { id: 'barcode', label: 'Código de Barras', tipo: 'barcode' },
    ];


    const [campos, setCampos] = useState([
        { id: 'header', label: 'Def-Software', x: 5, y: 5, fontSize: 16, visible: true, rotation: 0 },
        { id: 'nombreCompleto', label: 'Nombre Completo', x: 5, y: 15, fontSize: 14, visible: true, prefijo: '', rotation: 0 },
        { id: 'sector', label: 'Sector', x: 55, y: 5, fontSize: 10, visible: true, prefijo: '', rotation: 0 },
        { id: 'ubicacion', label: 'Ubicación', x: 5, y: 28, fontSize: 10, visible: true, prefijo: '', rotation: 0 },
        { id: 'plato', label: 'Plato', x: 5, y: 35, fontSize: 12, visible: true, prefijo: '', rotation: 0 },
        { id: 'fechaElaboracion', label: 'Elab.', x: 55, y: 28, fontSize: 9, visible: true, prefijo: 'Elaborado: ', rotation: 0 },
        { id: 'fechaVencimiento', label: 'Vto.', x: 55, y: 35, fontSize: 9, visible: true, prefijo: 'Consumir hasta: ', rotation: 0 },
    ]);



    const [selectedCampo, setSelectedCampo] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [plantillaNombre, setPlantillaNombre] = useState('');
    const [showPreview, setShowPreview] = useState(true);
    const [plantillasGuardadas, setPlantillasGuardadas] = useState([]);
    // const [showPlantillasList, setShowPlantillasList] = useState(false); // No usado actualmente
    const [pedidoPreviewIndex, setPedidoPreviewIndex] = useState(0);
    const [showNombreModal, setShowNombreModal] = useState(false);
    const [plantillaActiva, setPlantillaActiva] = useState(0);

    // Estados para navegación con mouse
    const [isDraggingCarousel, setIsDraggingCarousel] = useState(false);
    // const [dragStartX, setDragStartX] = useState(0); // No usado actualmente
    const [dragCurrentX, setDragCurrentX] = useState(0);
    const [rotacionCarrusel, setRotacionCarrusel] = useState(0);

    // Cargar configuración guardada
    useEffect(() => {
        const fetchPlantillas = async () => {
            try {
                const res = await axios.get(`${apiUrlEtiquetas}plantillas`);
                const data = res.data.map(p => ({
                    ...p,
                    ...normalizarConfigPlantilla(p.campos)
                }));
                setPlantillasGuardadas(data);

                // Cargar configuración activa
                const resActiva = await axios.get(`${apiUrlEtiquetas}plantillas/activa`);
                if (resActiva.data) {
                    const {
                        campos: camposArray,
                        tamanoEtiqueta: tamanoGuardado,
                    } = normalizarConfigPlantilla(resActiva.data.campos);
                    setCampos(asegurarCamposFecha(camposArray));
                    setTamanoSeleccionado(tamanoGuardado);
                    setTamanoAnterior(tamanoGuardado); // Evitar re-escalado al abrir el modal
                } else {
                    // Fallback a localStorage si no hay en DB
                    const savedConfig = localStorage.getItem('etiquetaConfig');
                    if (savedConfig) {
                        const parsed = JSON.parse(savedConfig);
                        setCampos(asegurarCamposFecha(parsed.campos));
                        if (parsed.tamanoEtiqueta) {
                            setTamanoSeleccionado(parsed.tamanoEtiqueta);
                            setTamanoAnterior(parsed.tamanoEtiqueta); // Evitar re-escalado
                        }
                    }
                }
            } catch (error) {
                console.error('Error al cargar plantillas desde DB:', error);
                // Fallback a localStorage
                const savedPlantillas = localStorage.getItem('etiquetaPlantillas');
                if (savedPlantillas) {
                    try {
                        const parsedPlantillas = JSON.parse(savedPlantillas);
                        const normalizadas = parsedPlantillas.map((p) => ({
                            ...p,
                            ...normalizarConfigPlantilla(p.campos),
                        }));
                        setPlantillasGuardadas(normalizadas);
                    } catch (fallbackError) {
                        console.error('Error cargando plantillas locales:', fallbackError);
                    }
                }

                const savedConfig = localStorage.getItem('etiquetaConfig');
                if (savedConfig) {
                    const parsed = JSON.parse(savedConfig);
                    setCampos(asegurarCamposFecha(parsed.campos));
                    if (parsed.tamanoEtiqueta) {
                        setTamanoSeleccionado(parsed.tamanoEtiqueta);
                        setTamanoAnterior(parsed.tamanoEtiqueta); // Evitar re-escalado
                    }
                }
            }
        };

        if (isOpen) {
            fetchPlantillas();
        }
    }, [isOpen, apiUrlEtiquetas]);

    const escalarCampos = useCallback((camposActuales, fromTamano, toTamano) => {
        const scaleX = toTamano.width / fromTamano.width;
        const scaleY = toTamano.height / fromTamano.height;
        const scaleBase = Math.min(scaleX, scaleY);
        const scaleFont = suavizarEscalaFuente(scaleBase);
        const maxFontSize = Math.max(20, Math.round(toTamano.height * 0.45 * 10) / 10);

        return camposActuales.map(c => ({
            ...c,
            x: Math.round(c.x * scaleX * 10) / 10,
            y: Math.round(c.y * scaleY * 10) / 10,
            fontSize: clamp(
                Math.round((c.fontSize || 16) * scaleFont * 10) / 10,
                6,
                maxFontSize
            ),
            qrSize: Math.round((c.qrSize || 30) * scaleBase * 10) / 10,
            barcodeHeight: Math.round((c.barcodeHeight || 50) * scaleY * 10) / 10,
        }));
    }, []);

    useEffect(() => {
        if (tamanoSeleccionado !== tamanoAnterior) {
            setCampos(prev => escalarCampos(prev, tamanoAnt, tamanoActual));
            setTamanoAnterior(tamanoSeleccionado);
        }
    }, [tamanoSeleccionado, escalarCampos, tamanoAnt, tamanoActual, tamanoAnterior]);

    const agregarCampo = (campoId) => {
        const campoDisponible = CAMPOS_DISPONIBLES.find(c => c.id === campoId);
        if (!campoDisponible) return;

        const nuevoCampo = {
            id: campoId,
            label: campoDisponible.label,
            x: 10,
            y: 10,
            fontSize: 16,
            visible: true,
            prefijo: '',
            rotation: 0,
            qrSize: 30, // Para códigos QR
            barcodeWidth: 2, // Para códigos de barras
            barcodeHeight: 50,
        };

        setCampos([...campos, nuevoCampo]);
    };

    const eliminarCampo = (index) => {
        const nuevosCampos = campos.filter((_, i) => i !== index);
        setCampos(nuevosCampos);
        if (selectedCampo === index) setSelectedCampo(null);
    };

    const actualizarCampo = (index, propiedad, valor) => {
        const nuevosCampos = [...campos];
        nuevosCampos[index] = { ...nuevosCampos[index], [propiedad]: valor };
        setCampos(nuevosCampos);
    };

    const toggleVisibilidad = (index) => {
        actualizarCampo(index, 'visible', !campos[index].visible);
    };

    const handleMouseDown = (index, e) => {
        e.preventDefault();
        setSelectedCampo(index);
        setIsDragging(true);
    };

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || selectedCampo === null) return;

        const preview = document.getElementById('label-preview');
        if (!preview) return;

        const rect = preview.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * LABEL_WIDTH;
        const y = ((e.clientY - rect.top) / rect.height) * LABEL_HEIGHT;

        setCampos(prevCampos => {
            const nuevosCampos = [...prevCampos];
            nuevosCampos[selectedCampo] = {
                ...nuevosCampos[selectedCampo],
                x: Math.max(0, Math.min(LABEL_WIDTH - 5, x)),
                y: Math.max(0, Math.min(LABEL_HEIGHT - 2, y))
            };
            return nuevosCampos;
        });
    }, [isDragging, selectedCampo, LABEL_WIDTH, LABEL_HEIGHT]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const guardarConfiguracion = async () => {
        try {
            const config = { campos, tamanoEtiqueta: tamanoSeleccionado };
            localStorage.setItem('etiquetaConfig', JSON.stringify(config));

            const activa = plantillasGuardadas.find(p => p.es_activo);
            if (activa) {
                await axios.put(`${apiUrlEtiquetas}plantillas/${activa.id}`, {
                    nombre: activa.nombre,
                    campos: JSON.stringify({ campos, tamanoEtiqueta: tamanoSeleccionado })
                });
            }

            alert('✅ Configuración guardada correctamente');
            if (onSaveConfig) onSaveConfig(config);
        } catch (error) {
            console.error('Error al guardar configuración:', error);
            const config = { campos, tamanoEtiqueta: tamanoSeleccionado };
            if (onSaveConfig) onSaveConfig(config);
            alert('⚠️ Guardado local exitoso, pero hubo un error sincronizando con el servidor');
        }
    };

    const guardarComoPlantilla = async () => {
        if (!plantillaNombre.trim()) {
            alert('⚠️ Por favor ingresa un nombre para la plantilla');
            return;
        }

        try {
            const nuevaPlantillaData = {
                nombre: plantillaNombre,
                campos: JSON.stringify({ campos, tamanoEtiqueta: tamanoSeleccionado }),
                usuario: nombreUsuario || 'admin'
            };

            const res = await axios.post(`${apiUrlEtiquetas}plantillas`, nuevaPlantillaData);

            const savedPlantilla = {
                ...res.data,
                campos: JSON.parse(res.data.campos)
            };

            const plantillasActualizadas = [...plantillasGuardadas, savedPlantilla];
            setPlantillasGuardadas(plantillasActualizadas);

            // Activar automáticamente la nueva
            await axios.post(`${apiUrlEtiquetas}plantillas/${savedPlantilla.id}/activar`);

            setPlantillaNombre('');
            setShowNombreModal(false);
            alert(`✅ Plantilla "${savedPlantilla.nombre}" guardada y activada correctamente`);
        } catch (error) {
            console.error('Error al guardar plantilla en DB:', error);
            alert('❌ Error al guardar la plantilla en el servidor');
        }
    };


    const abrirModalNombre = () => {
        setPlantillaNombre('');
        setShowNombreModal(true);
    };

    const cargarPlantilla = async (plantilla) => {
        try {
            const {
                campos: camposArray,
                tamanoEtiqueta: tamanoGuardado,
            } = normalizarConfigPlantilla(plantilla.campos);
            setCampos(camposArray);
            setTamanoSeleccionado(tamanoGuardado);
            const config = { campos: camposArray, tamanoEtiqueta: tamanoGuardado };
            localStorage.setItem('etiquetaConfig', JSON.stringify(config));

            // Notificar al servidor que esta es la activa
            await axios.post(`${apiUrlEtiquetas}plantillas/${plantilla.id}/activar`);

            // Notificar al padre (AbmPedidos)
            if (onSaveConfig) onSaveConfig(config);

            // Actualizar estado local de plantillas para reflejar es_activo
            const actualizadas = plantillasGuardadas.map(p => ({
                ...p,
                es_activo: p.id === plantilla.id
            }));
            setPlantillasGuardadas(actualizadas);

            alert(`✅ Plantilla "${plantilla.nombre}" cargada y aplicada`);
        } catch (error) {
            console.error('Error al activar plantilla:', error);
            alert('⚠️ Plantilla cargada localmente pero no pudo marcarse como activa en el servidor');
        }
    };


    const navegarPlantilla = useCallback((direccion) => {
        if (direccion === 'next') {
            setPlantillaActiva((prev) => (prev + 1) % plantillasGuardadas.length);
        } else {
            setPlantillaActiva((prev) => (prev - 1 + plantillasGuardadas.length) % plantillasGuardadas.length);
        }
    }, [plantillasGuardadas.length]);

    // Sincronizar rotación cuando cambia plantilla activa
    useEffect(() => {
        if (plantillasGuardadas.length > 0) {
            const anguloPlantilla = 360 / plantillasGuardadas.length;
            setRotacionCarrusel(-plantillaActiva * anguloPlantilla);
        }
    }, [plantillaActiva, plantillasGuardadas.length]);

    // Funciones para navegación con mouse - Efecto esfera circular
    const handleCarouselMouseDown = (e) => {
        setIsDraggingCarousel(true);
        setDragCurrentX(e.clientX);
    };

    const handleCarouselMouseMove = useCallback((e) => {
        if (!isDraggingCarousel) return;
        const deltaX = e.clientX - dragCurrentX;
        // Rotación continua mientras arrastras (positiva para que siga la dirección del mouse)
        setRotacionCarrusel(prev => prev + deltaX * 0.3);
        setDragCurrentX(e.clientX);
    }, [isDraggingCarousel, dragCurrentX]);

    const handleCarouselMouseUp = useCallback(() => {
        if (!isDraggingCarousel) return;

        // Calcular la plantilla más cercana y hacer snap suave
        const totalPlantillas = plantillasGuardadas.length;
        if (totalPlantillas === 0) return;

        const anguloPlantilla = 360 / totalPlantillas;
        const plantillaMasCercana = Math.round(-rotacionCarrusel / anguloPlantilla);

        // Actualizar índice activo basado en rotación (esto disparará el useEffect que ajusta la rotación)
        const nuevoIndice = ((plantillaMasCercana % totalPlantillas) + totalPlantillas) % totalPlantillas;
        setPlantillaActiva(nuevoIndice);

        setIsDraggingCarousel(false);
        setDragCurrentX(0);
    }, [isDraggingCarousel, rotacionCarrusel, plantillasGuardadas.length]);

    const handleCarouselWheel = useCallback((e) => {
        e.preventDefault();
        if (plantillasGuardadas.length <= 1) return;

        if (e.deltaY > 0) {
            // Scroll hacia abajo -> siguiente
            setPlantillaActiva((prev) => (prev + 1) % plantillasGuardadas.length);
        } else {
            // Scroll hacia arriba -> anterior
            setPlantillaActiva((prev) => (prev - 1 + plantillasGuardadas.length) % plantillasGuardadas.length);
        }
    }, [plantillasGuardadas.length]);

    // Agregar listeners para drag
    useEffect(() => {
        if (isDraggingCarousel) {
            document.addEventListener('mousemove', handleCarouselMouseMove);
            document.addEventListener('mouseup', handleCarouselMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleCarouselMouseMove);
            document.removeEventListener('mouseup', handleCarouselMouseUp);
        };
    }, [isDraggingCarousel, handleCarouselMouseMove, handleCarouselMouseUp]);

    const eliminarPlantilla = async (plantillaId) => {
        if (!window.confirm('¿Estás seguro de eliminar esta plantilla?')) return;

        try {
            await axios.delete(`${apiUrlEtiquetas}plantillas/${plantillaId}`);
            const plantillasActualizadas = plantillasGuardadas.filter(p => p.id !== plantillaId);
            setPlantillasGuardadas(plantillasActualizadas);
            alert('✅ Plantilla eliminada');
        } catch (error) {
            console.error('Error al eliminar plantilla:', error);
            alert('❌ Error al eliminar la plantilla del servidor');
        }
    };


    const exportarPlantilla = () => {
        const config = { campos, nombre: plantillaNombre || 'Plantilla sin nombre' };
        const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `plantilla-etiqueta-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const importarPlantilla = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const config = JSON.parse(event.target.result);
                setCampos(config.campos || config);
                alert('✅ Plantilla importada correctamente');
            } catch (error) {
                alert('❌ Error al importar plantilla');
                console.error(error);
            }
        };
        reader.readAsText(file);
    };

    if (!isOpen) return null;

    // Usar pedido real si está disponible, sino usar ejemplo
    const pedidoParaPreview = pedidosReales.length > 0
        ? pedidosReales[pedidoPreviewIndex]
        : {
            id_pedido: 123,
            nombreuser: 'Juan',
            apellido: 'Pérez',
            nombre: 'Milanesa con Papas',
            sector: 'Producción',
            ubicacion: 'Planta A',
            sede_nombre: 'San Isidro',
            sede_direccion: 'Avenida Andres Rolon 661',
            fecha: new Date().toISOString(),
        };

    return (
        <div className="modal-overlay">
            <div className="modal-configurador-etiquetas">
                <div className="modal-header">
                    <h2>⚙️ Configurador de Etiquetas</h2>
                    <button onClick={onClose} className="close-button">
                        <X size={24} />
                    </button>
                </div>

                <div className="configurador-content">
                    {/* Panel izquierdo: Controles */}
                    <div className="panel-controles">
                        <div className="seccion">
                            <h3>Campos Disponibles</h3>
                            <div className="campos-disponibles">
                                {CAMPOS_DISPONIBLES.map(campo => (
                                    <button
                                        key={campo.id}
                                        onClick={() => agregarCampo(campo.id)}
                                        className="btn-agregar-campo"
                                        title={`Agregar ${campo.label}`}
                                    >
                                        <Plus size={16} /> {campo.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="seccion">
                            <h3>Campos en Etiqueta</h3>
                            <div className="lista-campos">
                                {campos.map((campo, index) => (
                                    <div
                                        key={index}
                                        className={`campo-item ${selectedCampo === index ? 'selected' : ''}`}
                                        onClick={() => setSelectedCampo(index)}
                                    >
                                        <div className="campo-header">
                                            <span>{campo.label}</span>
                                            <div className="campo-actions">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); toggleVisibilidad(index); }}
                                                    className="btn-icon"
                                                    title={campo.visible ? 'Ocultar' : 'Mostrar'}
                                                >
                                                    {campo.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); eliminarCampo(index); }}
                                                    className="btn-icon btn-danger"
                                                    title="Eliminar"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>

                                        {selectedCampo === index && (
                                            <div className="campo-propiedades">
                                                <label>
                                                    Posición X (mm):
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max={LABEL_WIDTH}
                                                        step="0.5"
                                                        value={campo.x}
                                                        onChange={(e) => actualizarCampo(index, 'x', parseFloat(e.target.value))}
                                                    />
                                                </label>
                                                <label>
                                                    Posición Y (mm):
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max={LABEL_HEIGHT}
                                                        step="0.5"
                                                        value={campo.y}
                                                        onChange={(e) => actualizarCampo(index, 'y', parseFloat(e.target.value))}
                                                    />
                                                </label>

                                                {campo.id !== 'qrCode' && campo.id !== 'barcode' && campo.id !== 'separador' && (
                                                    <>
                                                        <label>
                                                            Tamaño Fuente:
                                                            <input
                                                                type="number"
                                                                min="8"
                                                                max="50"
                                                                value={campo.fontSize}
                                                                onChange={(e) => actualizarCampo(index, 'fontSize', parseInt(e.target.value))}
                                                            />
                                                        </label>
                                                        <label>
                                                            Rotación:
                                                            <select
                                                                value={campo.rotation || 0}
                                                                onChange={(e) => actualizarCampo(index, 'rotation', parseInt(e.target.value))}
                                                                className="select-rotation"
                                                            >
                                                                <option value={0}>0° (Normal)</option>
                                                                <option value={90}>90° (Derecha)</option>
                                                                <option value={180}>180° (Invertido)</option>
                                                                <option value={270}>270° (Izquierda)</option>
                                                            </select>
                                                        </label>
                                                    </>
                                                )}

                                                {campo.id === 'qrCode' && (
                                                    <label>
                                                        Tamaño QR (mm):
                                                        <input
                                                            type="number"
                                                            min="20"
                                                            max="50"
                                                            value={campo.qrSize || 30}
                                                            onChange={(e) => actualizarCampo(index, 'qrSize', parseInt(e.target.value))}
                                                        />
                                                    </label>
                                                )}

                                                {campo.id === 'barcode' && (
                                                    <>
                                                        <label>
                                                            Altura (mm):
                                                            <input
                                                                type="number"
                                                                min="30"
                                                                max="80"
                                                                value={campo.barcodeHeight || 50}
                                                                onChange={(e) => actualizarCampo(index, 'barcodeHeight', parseInt(e.target.value))}
                                                            />
                                                        </label>
                                                    </>
                                                )}

                                                {campo.id !== 'header' && campo.id !== 'separador' && campo.id !== 'qrCode' && campo.id !== 'barcode' && (
                                                    <label>
                                                        Prefijo:
                                                        <input
                                                            type="text"
                                                            value={campo.prefijo || ''}
                                                            onChange={(e) => actualizarCampo(index, 'prefijo', e.target.value)}
                                                            placeholder="Ej: Nombre: "
                                                        />
                                                    </label>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="seccion-acciones">
                            <button onClick={guardarConfiguracion} className="btn-accion btn-primary">
                                <Save size={18} /> Guardar Configuración
                            </button>

                            <div className="separador-seccion"></div>

                            <div className="plantilla-actions">
                                <button onClick={abrirModalNombre} className="btn-accion btn-success">
                                    <Save size={18} /> Guardar como Plantilla
                                </button>

                                <div className="separador-mini"></div>

                                <button onClick={exportarPlantilla} className="btn-accion btn-secondary">
                                    <Download size={18} /> Exportar JSON
                                </button>
                                <label className="btn-accion btn-secondary">
                                    <Upload size={18} /> Importar JSON
                                    <input
                                        type="file"
                                        accept=".json"
                                        onChange={importarPlantilla}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Panel derecho: Vista previa + Carrusel de plantillas */}
                    <div className="panel-preview">
                        {/* Vista Previa siempre visible */}
                        <div className="preview-section">
                            <div className="preview-header">
                                <h3>Vista Previa</h3>
                                <div className="preview-controls">
                                    <div className="tamano-selector">
                                        <label>Tamaño:</label>
                                        <select
                                            value={tamanoSeleccionado}
                                            onChange={(e) => setTamanoSeleccionado(e.target.value)}
                                            className="select-pedido"
                                        >
                                            {TAMANOS_ETIQUETA.map(t => (
                                                <option key={t.id} value={t.id}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {pedidosReales.length > 0 && (
                                        <div className="pedido-selector">
                                            <label>Pedido:</label>
                                            <select
                                                value={pedidoPreviewIndex}
                                                onChange={(e) => setPedidoPreviewIndex(parseInt(e.target.value))}
                                                className="select-pedido"
                                            >
                                                {pedidosReales.map((pedido, idx) => (
                                                    <option key={idx} value={idx}>
                                                        #{pedido.id_pedido} - {pedido.nombreuser} - {pedido.nombre}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <button
                                        onClick={() => setShowPreview(!showPreview)}
                                        className="btn-toggle-preview"
                                    >
                                        {showPreview ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            {showPreview && (
                                <div
                                    id="label-preview"
                                    className="label-preview"
                                    style={{
                                        width: `${(LABEL_WIDTH / 100) * 400}px`,
                                        height: `${(LABEL_HEIGHT / 50) * 200}px`,
                                        border: '2px solid #333',
                                        position: 'relative',
                                        backgroundColor: '#fff',
                                        cursor: isDragging ? 'grabbing' : 'default',
                                    }}
                                >
                                    {campos.filter(c => c.visible).map((campo, index) => {
                                        const xPercent = (campo.x / LABEL_WIDTH) * 100;
                                        const yPercent = (campo.y / LABEL_HEIGHT) * 100;

                                        let texto = '';
                                        switch (campo.id) {
                                            case 'header':
                                                texto = 'Def-Software';
                                                break;
                                            case 'nombre':
                                                texto = `${campo.prefijo || ''}${pedidoParaPreview.nombreuser || ''}`;
                                                break;
                                            case 'apellido':
                                                texto = `${campo.prefijo || ''}${pedidoParaPreview.apellido || ''}`;
                                                break;
                                            case 'nombreCompleto':
                                                texto = `${campo.prefijo || ''}${pedidoParaPreview.nombreuser || ''} ${pedidoParaPreview.apellido || ''}`;
                                                break;
                                            case 'plato':
                                                texto = `${campo.prefijo || ''}${pedidoParaPreview.nombre || ''}`;
                                                break;
                                            case 'tipoPlato':
                                                texto = `${campo.prefijo || ''}${pedidoParaPreview.tipo || ''}`;
                                                break;
                                            case 'sector':
                                                texto = `${campo.prefijo || ''}${pedidoParaPreview.sector || ''}`;
                                                break;
                                            case 'ubicacion':
                                                texto = `${campo.prefijo || ''}${getUbicacionEtiqueta(pedidoParaPreview)}`;
                                                break;
                                            case 'sede':
                                                texto = `${campo.prefijo || ''}${pedidoParaPreview.sede_nombre || 'Sede'}`;
                                                break;
                                            case 'sede_direccion':
                                                texto = `${campo.prefijo || ''}${getDireccionEtiqueta(pedidoParaPreview) || 'Dirección de Sede'}`;
                                                break;
                                            case 'fechaElaboracion':
                                                const fechaElab = pedidoParaPreview.fecha
                                                    ? (() => { const [a, m, d] = pedidoParaPreview.fecha.split('-').map(Number); return new Date(a, m - 1, d); })()
                                                    : new Date();
                                                texto = `${campo.prefijo || 'Elaborado: '}${fechaElab.toLocaleDateString('es-AR')}`;
                                                break;
                                            case 'fechaVencimiento':
                                                const fechaVto = pedidoParaPreview.fecha
                                                    ? (() => { const [a, m, d] = pedidoParaPreview.fecha.split('-').map(Number); const f = new Date(a, m - 1, d); f.setDate(f.getDate() + 1); return f; })()
                                                    : (() => { const f = new Date(); f.setDate(f.getDate() + 1); return f; })();
                                                texto = `${campo.prefijo || 'Consumir hasta: '}${fechaVto.toLocaleDateString('es-AR')}`;
                                                break;
                                            default:
                                                texto = campo.label;
                                        }

                                        // Línea separadora
                                        if (campo.id === 'separador') {
                                            return (
                                                <div
                                                    key={index}
                                                    style={{
                                                        position: 'absolute',
                                                        left: `${xPercent}%`,
                                                        top: `${yPercent}%`,
                                                        width: '80%',
                                                        height: '2px',
                                                        backgroundColor: '#000',
                                                        cursor: 'grab',
                                                    }}
                                                    onMouseDown={(e) => handleMouseDown(campos.indexOf(campo), e)}
                                                />
                                            );
                                        }

                                        // Código QR
                                        if (campo.id === 'qrCode') {
                                            const qrSize = ((campo.qrSize || 30) / LABEL_HEIGHT) * 100;
                                            return (
                                                <div
                                                    key={index}
                                                    style={{
                                                        position: 'absolute',
                                                        left: `${xPercent}%`,
                                                        top: `${yPercent}%`,
                                                        width: `${qrSize}%`,
                                                        height: `${qrSize}%`,
                                                        border: '2px solid #000',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'grab',
                                                        backgroundColor: '#fff',
                                                    }}
                                                    onMouseDown={(e) => handleMouseDown(campos.indexOf(campo), e)}
                                                >
                                                    <QrCode size={20} />
                                                </div>
                                            );
                                        }

                                        // Código de Barras
                                        if (campo.id === 'barcode') {
                                            const barcodeWidth = 35;
                                            const barcodeHeight = ((campo.barcodeHeight || 50) / LABEL_HEIGHT) * 100;
                                            return (
                                                <div
                                                    key={index}
                                                    style={{
                                                        position: 'absolute',
                                                        left: `${xPercent}%`,
                                                        top: `${yPercent}%`,
                                                        width: `${barcodeWidth}%`,
                                                        height: `${barcodeHeight}%`,
                                                        border: '1px solid #000',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'grab',
                                                        backgroundColor: '#fff',
                                                    }}
                                                    onMouseDown={(e) => handleMouseDown(campos.indexOf(campo), e)}
                                                >
                                                    <div style={{ fontSize: '8px', fontFamily: 'monospace' }}>|||||||||||||||</div>
                                                    <div style={{ fontSize: '8px' }}>#{pedidoParaPreview.id_pedido || '000'}</div>
                                                </div>
                                            );
                                        }

                                        // Texto normal
                                        const rotation = campo.rotation || 0;
                                        return (
                                            <div
                                                key={index}
                                                style={{
                                                    position: 'absolute',
                                                    left: `${xPercent}%`,
                                                    top: `${yPercent}%`,
                                                    fontSize: `${campo.fontSize * 1.2}px`,
                                                    fontWeight: campo.id === 'header' ? 'bold' : 'normal',
                                                    cursor: 'grab',
                                                    userSelect: 'none',
                                                    border: selectedCampo === campos.indexOf(campo) ? '2px dashed #007bff' : 'none',
                                                    padding: '2px',
                                                    transform: `rotate(${rotation}deg)`,
                                                    transformOrigin: 'left top',
                                                    color: '#000',
                                                    lineHeight: '1.2',
                                                }}
                                                onMouseDown={(e) => handleMouseDown(campos.indexOf(campo), e)}
                                            >
                                                {texto}
                                                {selectedCampo === campos.indexOf(campo) && (
                                                    <Move
                                                        size={12}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '-15px',
                                                            left: '0',
                                                            color: '#007bff',
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="preview-info">
                                <p><strong>Dimensiones:</strong> {LABEL_WIDTH}mm x {LABEL_HEIGHT}mm</p>
                                <p><strong>Resolución:</strong> {DPI} DPI</p>
                                <p><strong>Tip:</strong> Haz clic y arrastra los campos en la vista previa para reposicionarlos</p>
                            </div>
                        </div>

                        {/* Carrusel 3D de Plantillas - Siempre visible debajo de la vista previa */}
                        {plantillasGuardadas.length > 0 && (
                            <div className="carousel-integrado-container">
                                <div className="carousel-integrado-header">
                                    <h3>✨ Mis Plantillas</h3>
                                </div>

                                <div
                                    className={`carousel-3d-integrado ${isDraggingCarousel ? 'dragging' : ''}`}
                                    onMouseDown={handleCarouselMouseDown}
                                    onWheel={handleCarouselWheel}
                                    style={{ cursor: isDraggingCarousel ? 'grabbing' : 'grab' }}
                                >
                                    <div
                                        className="carousel-track-integrado"
                                        style={{
                                            transform: `translateZ(-350px) rotateY(${rotacionCarrusel}deg)`,
                                            transition: isDraggingCarousel ? 'none' : 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                                        }}
                                    >
                                        {plantillasGuardadas.map((plantilla, index) => {
                                            const totalPlantillas = plantillasGuardadas.length;
                                            const anguloPlantilla = 360 / totalPlantillas;
                                            const angulo = index * anguloPlantilla;
                                            const isActive = index === plantillaActiva;
                                            const plantillaCampos = Array.isArray(plantilla.campos)
                                                ? plantilla.campos
                                                : normalizarConfigPlantilla(plantilla.campos).campos;

                                            return (
                                                <div
                                                    key={plantilla.id}
                                                    className={`plantilla-card-integrada ${isActive ? 'active' : ''}`}
                                                    style={{
                                                        transform: `
                                                                    rotateY(${angulo}deg)
                                                                    translateZ(350px)
                                                                    ${isActive ? 'scale(1)' : 'scale(0.85)'}
                                                                `,
                                                        opacity: isActive ? 1 : 0.7
                                                    }}
                                                    onClick={() => setPlantillaActiva(index)}
                                                >
                                                    <div className="etiqueta-preview-carousel">
                                                        <div className="etiqueta-simulada-carousel">
                                                            {plantillaCampos.filter(c => c.visible).slice(0, 10).map((campo, idx) => {
                                                                const pedidoEjemplo = {
                                                                    nombreuser: 'Juan',
                                                                    apellido: 'Pérez',
                                                                    nombre: 'Milanesa c/Papas',
                                                                    sector: 'Producción',
                                                                    ubicacion: 'Planta A',
                                                                    id_pedido: 123,
                                                                    fecha: new Date().toISOString()
                                                                };

                                                                let contenido = '';
                                                                switch (campo.id) {
                                                                    case 'header':
                                                                        contenido = 'CIASA';
                                                                        break;
                                                                    case 'nombre':
                                                                        contenido = `${campo.prefijo || ''}${pedidoEjemplo.nombreuser}`;
                                                                        break;
                                                                    case 'apellido':
                                                                        contenido = `${campo.prefijo || ''}${pedidoEjemplo.apellido}`;
                                                                        break;
                                                                    case 'nombreCompleto':
                                                                        contenido = `${campo.prefijo || ''}${pedidoEjemplo.nombreuser} ${pedidoEjemplo.apellido}`;
                                                                        break;
                                                                    case 'plato':

                                                                        contenido = `${campo.prefijo || ''}${pedidoEjemplo.nombre}`;
                                                                        break;
                                                                    case 'tipoPlato':
                                                                        contenido = `${campo.prefijo || ''}${pedidoEjemplo.tipo || ''}`;
                                                                        break;
                                                                    case 'sector':
                                                                        contenido = `${campo.prefijo || ''}${pedidoEjemplo.sector}`;
                                                                        break;
                                                                    case 'ubicacion':
                                                                        contenido = `${campo.prefijo || ''}${pedidoEjemplo.ubicacion}`;
                                                                        break;
                                                                    case 'sede':
                                                                        contenido = `${campo.prefijo || ''}${pedidoEjemplo.sede_nombre || 'Sede'}`;
                                                                        break;
                                                                    case 'sede_direccion':
                                                                        contenido = `${campo.prefijo || ''}${pedidoEjemplo.sede_direccion || 'Dirección de Sede'}`;
                                                                        break;
                                                                    case 'fechaElaboracion':
                                                                        contenido = `${campo.prefijo || 'Elaborado: '}${new Date().toLocaleDateString('es-AR')}`;
                                                                        break;
                                                                    case 'fechaVencimiento':
                                                                        const fechaV = new Date();
                                                                        fechaV.setDate(fechaV.getDate() + 1);
                                                                        contenido = `${campo.prefijo || 'Consumir hasta: '}${fechaV.toLocaleDateString('es-AR')}`;
                                                                        break;
                                                                    case 'qrCode':
                                                                        return (
                                                                            <div
                                                                                key={idx}
                                                                                style={{
                                                                                    position: 'absolute',
                                                                                    left: `${(campo.x / 100) * 100}%`,
                                                                                    top: `${(campo.y / 50) * 100}%`,
                                                                                    width: '25%',
                                                                                    height: '25%',
                                                                                }}
                                                                            >
                                                                                <div className="qr-preview-carousel">QR</div>
                                                                            </div>
                                                                        );
                                                                    case 'barcode':
                                                                        return (
                                                                            <div
                                                                                key={idx}
                                                                                style={{
                                                                                    position: 'absolute',
                                                                                    left: `${(campo.x / 100) * 100}%`,
                                                                                    top: `${(campo.y / 50) * 100}%`,
                                                                                    width: '35%',
                                                                                    height: '20%',
                                                                                }}
                                                                            >
                                                                                <div className="barcode-preview-carousel">|||||||</div>
                                                                            </div>
                                                                        );
                                                                    case 'separador':
                                                                        return (
                                                                            <div
                                                                                key={idx}
                                                                                style={{
                                                                                    position: 'absolute',
                                                                                    left: `${(campo.x / 100) * 100}%`,
                                                                                    top: `${(campo.y / 50) * 100}%`,
                                                                                    width: '80%',
                                                                                    height: '1px',
                                                                                    background: '#000',
                                                                                }}
                                                                            />
                                                                        );
                                                                    default:
                                                                        contenido = campo.label;
                                                                }

                                                                return (
                                                                    <div
                                                                        key={idx}
                                                                        className="campo-etiqueta-carousel"
                                                                        style={{
                                                                            position: 'absolute',
                                                                            left: `${(campo.x / 100) * 100}%`,
                                                                            top: `${(campo.y / 50) * 100}%`,
                                                                            fontSize: `${campo.fontSize * 0.38}px`,
                                                                            fontWeight: campo.id === 'header' ? 'bold' : 'normal',
                                                                            transform: `rotate(${campo.rotation || 0}deg)`,
                                                                            transformOrigin: 'left top',
                                                                        }}
                                                                    >
                                                                        {contenido}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Información de la plantilla activa */}
                                <div className="plantilla-info-integrada">
                                    <h4>{plantillasGuardadas[plantillaActiva].nombre}</h4>
                                    <p className="plantilla-detalles">
                                        {plantillasGuardadas[plantillaActiva].campos.length} campos • {LABEL_WIDTH}x{LABEL_HEIGHT}mm •
                                        {' ' + new Date(plantillasGuardadas[plantillaActiva].fecha).toLocaleDateString('es-AR')}
                                    </p>
                                </div>

                                {/* Controles del carrusel */}
                                <div className="carousel-controls-integrado">
                                    <button
                                        onClick={() => navegarPlantilla('prev')}
                                        className="carousel-nav-btn-integrado prev-btn"
                                        disabled={plantillasGuardadas.length <= 1}
                                    >
                                        ◀
                                    </button>

                                    <div className="carousel-info-integrado">
                                        <div className="carousel-counter-integrado">
                                            {plantillaActiva + 1} / {plantillasGuardadas.length}
                                        </div>
                                        <div className="carousel-indicators-integrado">
                                            {plantillasGuardadas.map((_, index) => (
                                                <button
                                                    key={index}
                                                    className={`indicator-integrado ${index === plantillaActiva ? 'active' : ''}`}
                                                    onClick={() => setPlantillaActiva(index)}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => navegarPlantilla('next')}
                                        className="carousel-nav-btn-integrado next-btn"
                                        disabled={plantillasGuardadas.length <= 1}
                                    >
                                        ▶
                                    </button>
                                </div>

                                {/* Acciones de la plantilla activa */}
                                <div className="plantilla-acciones-integradas">
                                    <button
                                        onClick={() => cargarPlantilla(plantillasGuardadas[plantillaActiva])}
                                        className="btn-accion-integrada btn-cargar"
                                    >
                                        ✓ Cargar Plantilla
                                    </button>
                                    <button
                                        onClick={() => eliminarPlantilla(plantillasGuardadas[plantillaActiva].id)}
                                        className="btn-accion-integrada btn-eliminar"
                                    >
                                        🗑️ Eliminar
                                    </button>
                                </div>

                                <div className="carousel-hint-integrado">
                                    💡 Arrastra, usa la rueda del mouse, las flechas o los indicadores
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Mini Modal para nombre de plantilla */}
            {showNombreModal && (
                <div className="mini-modal-overlay" onClick={() => setShowNombreModal(false)}>
                    <div className="mini-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="mini-modal-header">
                            <h3>💾 Guardar Plantilla</h3>
                            <button onClick={() => setShowNombreModal(false)} className="close-button">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="mini-modal-body">
                            <label>
                                <strong>Nombre de la plantilla:</strong>
                            </label>
                            <input
                                type="text"
                                placeholder="Ej: Plantilla Comedor"
                                value={plantillaNombre}
                                onChange={(e) => setPlantillaNombre(e.target.value)}
                                className="input-plantilla-modal"
                                autoFocus
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && plantillaNombre.trim()) {
                                        guardarComoPlantilla();
                                    }
                                }}
                            />
                        </div>
                        <div className="mini-modal-footer">
                            <button onClick={() => setShowNombreModal(false)} className="btn-modal btn-cancel">
                                Cancelar
                            </button>
                            <button
                                onClick={guardarComoPlantilla}
                                className="btn-modal btn-save"
                                disabled={!plantillaNombre.trim()}
                            >
                                <Save size={16} /> Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConfiguradorEtiquetas;
